import React, { useState, useCallback, useContext, createContext } from "react";
import {
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { toast } from "react-toastify";

const DataContext = createContext();

const DEFAULT_PAGE_SIZE = 10;

export const AdminDataProvider = ({ children }) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState({});
  const [paginationCursors, setPaginationCursors] = useState({});
  const [hasMore, setHasMore] = useState({});
  const [sortConfig, setSortConfig] = useState({});

  const fetchFirestorePage = useCallback(
    async (
      collectionName,
      pageSize,
      lastVisibleDoc = null,
      currentSortConfig
    ) => {
      const { field: sortField, order: sortOrder } = currentSortConfig || {
        field: "createdAt",
        order: "desc",
      };

      console.log(
        `🔥 Firestore SDK Fetch: ${collectionName}, Sort: ${sortField} ${sortOrder}, After: ${
          lastVisibleDoc?.id || "Start"
        }`
      );

      const collectionRef = collection(db, collectionName);
      const queryConstraints = [orderBy(sortField, sortOrder), limit(pageSize)];

      if (lastVisibleDoc) {
        queryConstraints.push(startAfter(lastVisibleDoc));
      }

      const q = query(collectionRef, ...queryConstraints);

      try {
        const snapshot = await getDocs(q);
        const newDocs = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        const newLastVisibleSnapshot = snapshot.docs[snapshot.docs.length - 1];
        const hasMoreData = snapshot.docs.length === pageSize;

        console.log(
          `✅ Firestore SDK Fetch Success: ${collectionName}, Fetched: ${newDocs.length}`
        );

        return { newDocs, newLastVisibleSnapshot, hasMoreData };
      } catch (error) {
        console.error(
          `❌ Firestore SDK Fetch Error (${collectionName}) on sort by "${sortField}":`,
          error.message
        );
        if (error.code === "failed-precondition") {
          throw new Error(
            `Query failed. You may need to create a Firestore index for the field '${sortField}'. Check the console for a link.`
          );
        }
        throw error;
      }
    },
    []
  );

  const refetchWithNewSort = useCallback(
    async (collectionName, newSortConfig) => {
      console.log(
        `🔄 Triggering refetch for ${collectionName} with new sort`,
        newSortConfig
      );
      setLoading((prev) => ({ ...prev, [collectionName]: true }));
      // This line is important to clear out ONLY the data for the current collection
      setData((prev) => ({ ...prev, [collectionName]: [] }));
      setPaginationCursors((prev) => ({ ...prev, [collectionName]: null }));
      setHasMore((prev) => ({ ...prev, [collectionName]: true }));

      try {
        const { newDocs, newLastVisibleSnapshot, hasMoreData } =
          await fetchFirestorePage(
            collectionName,
            DEFAULT_PAGE_SIZE,
            null,
            newSortConfig
          );

        // --- FIX #1: Use functional updates to avoid overwriting other collections' state ---
        setData((prev) => ({ ...prev, [collectionName]: newDocs }));
        setPaginationCursors((prev) => ({
          ...prev,
          [collectionName]: newLastVisibleSnapshot || null,
        }));
        setHasMore((prev) => ({ ...prev, [collectionName]: hasMoreData }));
      } catch (error) {
        console.error(
          `❌ Error during sort refetch for ${collectionName}:`,
          error
        );
      } finally {
        setLoading((prev) => ({ ...prev, [collectionName]: false }));
      }
    },
    [fetchFirestorePage]
  );

  const setSortForCollection = useCallback(
    (collectionName, field, order) => {
      const newSortConfig = { field, order };
      setSortConfig((prev) => ({ ...prev, [collectionName]: newSortConfig }));
      refetchWithNewSort(collectionName, newSortConfig);
    },
    [refetchWithNewSort]
  );

  const ensureCollectionData = useCallback(
    async (collectionName) => {
      if (
        loading[collectionName] ||
        (data[collectionName] && data[collectionName].length > 0)
      ) {
        return;
      }
      await refetchWithNewSort(collectionName, sortConfig[collectionName]);
    },
    [loading, data, sortConfig, refetchWithNewSort]
  );

  const fetchMoreData = useCallback(
    async (collectionName) => {
      if (loading[collectionName] || !hasMore[collectionName]) {
        return;
      }
      setLoading((prev) => ({ ...prev, [collectionName]: true }));

      const lastVisibleDoc = paginationCursors[collectionName];
      const currentSort = sortConfig[collectionName];

      try {
        const { newDocs, newLastVisibleSnapshot, hasMoreData } =
          await fetchFirestorePage(
            collectionName,
            DEFAULT_PAGE_SIZE,
            lastVisibleDoc,
            currentSort
          );

        if (newDocs.length > 0) {
          // --- FIX #2: Use functional updates here as well for consistency and safety ---
          setData((prev) => ({
            ...prev,
            [collectionName]: [...(prev[collectionName] || []), ...newDocs],
          }));
          setPaginationCursors((prev) => ({
            ...prev,
            [collectionName]: newLastVisibleSnapshot || null,
          }));
          setHasMore((prev) => ({ ...prev, [collectionName]: hasMoreData }));
        } else {
          setHasMore((prev) => ({ ...prev, [collectionName]: false }));
        }
      } catch (error) {
        console.error(
          `❌ Error fetching more data for ${collectionName}:`,
          error
        );
      } finally {
        setLoading((prev) => ({ ...prev, [collectionName]: false }));
      }
    },
    [loading, hasMore, paginationCursors, sortConfig, fetchFirestorePage]
  );

  const refetchCollection = useCallback(
    async (collectionName) => {
      await refetchWithNewSort(collectionName, sortConfig[collectionName]);
    },
    [sortConfig, refetchWithNewSort]
  );

  const deleteDocumentState = useCallback((collectionName, docId) => {
    setData((prev) => {
      const currentDocs = prev[collectionName] || [];
      const updatedDocs = currentDocs.filter((doc) => doc.id !== docId);
      return { ...prev, [collectionName]: updatedDocs };
    });
  }, []);

  const upsertDocumentState = useCallback(
    (collectionName, docData) => {
      setData((prev) => {
        const currentDocs = prev[collectionName] || [];
        const index = currentDocs.findIndex((d) => d.id === docData.id);

        if (index > -1) {
          console.log(
            `✅ Optimistic UPDATE for ${docData.id}. No refetch needed.`
          );
          const newDocs = [...currentDocs];
          newDocs[index] = { ...newDocs[index], ...docData };
          return { ...prev, [collectionName]: newDocs };
        } else {
          const currentSort = prev.sortConfig?.[collectionName] || {
            field: "createdAt",
            order: "desc",
          };

          if (
            currentSort.field === "createdAt" &&
            currentSort.order === "desc"
          ) {
            console.log(`➕ Optimistic ADD for ${docData.id} (prepending).`);
            const newDocs = [docData, ...currentDocs];
            return { ...prev, [collectionName]: newDocs };
          } else {
            console.log(
              `➕ New item added while sorted by ${currentSort.field}. Triggering refetch.`
            );
            toast.info("Refreshing list to show new item in correct order...");
            refetchWithNewSort(collectionName, currentSort);
            return prev;
          }
        }
      });
    },
    [refetchWithNewSort]
  );

  return (
    <DataContext.Provider
      value={{
        data,
        loading,
        hasMore,
        ensureCollectionData,
        fetchMoreData,
        refetchCollection,
        deleteDocumentState,
        upsertDocumentState,
        setSortForCollection,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => useContext(DataContext);
